import { CAMPAIGN_NAME_FOR_SERVICE_REQUEST } from 'src/constants/options';
import { ACTIVATION_BUSINESS_UNIT, DISPLAY_ADS_TYPE, SERVICE_TYPE } from '../constants/ActivationConstants';
import { isOrmValidDateFormat } from './DateTimeHelper';
import { formatBookCoverImage } from './Helper';
import { BOOK_PRODUCT_TYPE_AUDIO, BOOK_PRODUCT_TYPES } from './retailerProductType';
import { isValidURL } from './UrlHelper';
import capitalize from 'lodash/capitalize';
import countryList from 'react-select-country-list';

const countryOptions = countryList().getData();

//This method will accept headerfields from csv and list of requirefields and check if all required fields exists in given array or not
export const validateCsvHeaders = (headerFields, requiredFields) =>
  requiredFields.filter(x => !headerFields.some(header => header.trim().toLowerCase() === x.trim().toLowerCase()));

export const trimCsvData = data => {
  return data.map(obj => Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.trim(), value])));
};
//For external title csv, we need either Primary ISBN or ASIN, this method will check if one of the value exist
export const validateCsvOptionalHeaders = (headerFields, oneOfRequireFields) => {
  const headerFieldsSet = new Set(headerFields);
  return oneOfRequireFields.some(el => headerFieldsSet.has(el));
};

//This method will take requiredFields array and data array. Check if any field is empty value
export const validateCsvData = (requiredFields, data) => {
  let errorsPerTitle = [];
  let identifier = requiredFields.filter(x => x.isIdentifier)[0];
  requiredFields.forEach(field => {
    const fieldName = field.fieldName;
    //Check require field value exists
    let result = data.filter(x => x[fieldName] === '' || !x[fieldName]);
    if (result.length > 0) {
      result.forEach(value => {
        errorsPerTitle.push({ title: value[identifier.fieldName], error: `${capitalize(fieldName)} is missing !!!` });
      });
    } else {
      //If field needs to be specific length
      if (field.checkLength) {
        result = data.filter(x => x[fieldName].length !== field.checkLength);
        if (result.length > 0) {
          result.forEach(value => {
            errorsPerTitle.push({
              title: value[identifier.fieldName],
              error: `${capitalize(fieldName)} length should be ${field.checkLength} !!!`,
            });
          });
        }
      }

      //If field needs to be number data type
      if (field.isNumberCheck) {
        result = data.filter(x => isNaN(x[fieldName]));
        if (result.length > 0) {
          result.forEach(value => {
            errorsPerTitle.push({
              title: value[identifier.fieldName],
              error: `${capitalize(fieldName)} should be number. $ sign not allowed !!!`,
            });
          });
        }
      }

      //check field value is valid or not
      if (field.checkValues && field.checkValues.length > 0) {
        result = data.filter(x => validateString(x[fieldName], field.checkValues));
        if (result.length > 0) {
          result.forEach(value => {
            errorsPerTitle.push({
              title: value[identifier.fieldName],
              error: `${capitalize(fieldName)} should be: ${field.checkValues.join(', ')}`,
            });
          });
        }
      }
      //if field needs to be boolean

      if (field.isBooleanCheck) {
        result = data.filter(x => !Boolean(x[field.fieldName]));

        if (result.length > 0)
          errorsPerTitle.push({
            title: result[0][identifier.fieldName],
            error: `${field.fieldName} can only be true or false !!!`,
          });
      }
      if (field.isDateCheck) {
        result = data.filter(
          x => new Date(x[field.fieldName]) === 'Invalid Date' && isNaN(new Date(x[field.fieldName]))
        );

        if (result.length > 0)
          errorsPerTitle.push({
            title: result[0][identifier.fieldName],
            error: `${field.fieldName} needs to be valid date !!!`,
          });
      }

      // Validate country array check
      if (field.isCountryArrayCheck) {
        const validCountryCodes = new Set(countryOptions.map(option => option.value));
        result = data.filter(x => {
          const fieldValue = x[field.fieldName];
          if (!fieldValue) return false; // Skip empty values (already checked above)

          // Split by comma and trim each country code
          const countries = fieldValue.split(',').map(value => value?.trim());

          // Check if any country is invalid
          const invalidCountries = countries.filter(value => {
            // "ALL" is considered valid
            if (value.toUpperCase() === 'ALL') return false;
            // Country codes not having length of 2 are invalid
            if (value.length !== 2) return true;
            // Country codes of length 2 that are not in the validCountryCodes set are invalid
            return !validCountryCodes.has(value);
          });

          return invalidCountries.length > 0;
        });

        if (result.length > 0) {
          result.forEach(value => {
            const fieldValue = value[field.fieldName];
            const countries = fieldValue.split(',').map(c => c?.trim());
            const validCountryCodes = new Set(countryOptions.map(option => option.value));
            const invalidCountries = countries.filter(c => {
              if (c.toUpperCase() === 'ALL') return false;
              if (c.length !== 2) return true;
              return !validCountryCodes.has(c);
            });

            errorsPerTitle.push({
              title: value[identifier.fieldName],
              error: `${capitalize(fieldName)} has invalid country code(s): ${invalidCountries.join(
                ', '
              )}. Must be comma-separated 2-letter country codes !!!`,
            });
          });
        }
      }
    }
  });
  return errorsPerTitle;
};

// /ORMTECH-2050: refer this ticket to check Mandatory field
// `retailerNames` are the book retailers (`/bookretailers`) a dedicated email's Retailers may name.
export const validateActivationCSVData = (data, { retailerNames = [] } = {}) => {
  let errors = [];
  data.forEach((value, index) => {
    let errorObj = value;

    // common validation
    const commonValidation = [
      { fieldName: 'Service Type', checkValues: Object.values(SERVICE_TYPE), isRequired: true },
      { fieldName: 'Display Ads Type', checkValues: DISPLAY_ADS_TYPE },
      { fieldName: 'Url', isUrlCheck: true },
      { fieldName: 'BusUnit', checkValues: Array.from(ACTIVATION_BUSINESS_UNIT.keys()), isRequired: true },
      {
        fieldName: 'Start Date',
        isDateCheck: true,
        startDateEndDateCheck: true,
        isRequired: true,
      },
      {
        fieldName: 'End Date',
        isDateCheck: true,
        startDateEndDateCheck: true,
        isRequired: true,
      },
      { fieldName: 'Countries', isCountryArrayCheck: true, isRequired: true },
    ];
    errorObj = { ...errorObj, ...validateCSVRowData(commonValidation, value) };

    if (value['Service Type'] === SERVICE_TYPE.DISPLAY_ADS) {
      const validationConfig = [
        { fieldName: 'Display Ads Type', isRequired: true },
        { fieldName: 'Url', isRequired: true },
      ];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }

    if (value['Service Type'] === SERVICE_TYPE.SPONSORED_CONTENT || value['Service Type'] === SERVICE_TYPE.GIVEAWAYS) {
      const validationConfig = [{ fieldName: 'Url', isRequired: true }];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }

    if (value['Service Type'] === SERVICE_TYPE.DEDICATED_EMAILS) {
      const validationConfig = [
        { fieldName: 'Title', isRequired: true },
        { fieldName: 'Authors', isRequired: true },
        { fieldName: 'ISBN', checkLength: 13 },
        { fieldName: 'ASIN', checkLength: 10 },
        { fieldName: 'ISBN/ASIN', checkISBNOrASIN: true },
        { fieldName: 'SaleDLP', isNumberCheck: true },
        { fieldName: 'DLP', isNumberCheck: true },
        // Optional (blank promotes the title at every retailer), but each one given must be a known retailer.
        { fieldName: 'Retailers', isRetailerArrayCheck: true, retailerNames },
      ];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }

    if (value['Service Type'] === SERVICE_TYPE.SOLD_SLOT) {
      let validationConfig = [
        { fieldName: 'Title', isIdentifier: true, isRequired: true },
        { fieldName: 'Authors', isRequired: true },
        { fieldName: 'ISBN', checkLength: 13 },
        { fieldName: 'ASIN', checkLength: 10 },
        { fieldName: 'ISBN/ASIN', checkISBNOrASIN: true },
        { fieldName: 'Audible ASIN', checkLength: 10 },
        { fieldName: 'SaleDLP', isNumberCheck: true, isRequired: true },
        { fieldName: 'DLP', isNumberCheck: true, isRequired: true },
        { fieldName: 'Product Type', checkValues: BOOK_PRODUCT_TYPES, isCaseInsensitive: true, isRequired: true },
        { fieldName: 'Campaign Name', checkValues: CAMPAIGN_NAME_FOR_SERVICE_REQUEST.map(value => value.value) },
      ];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }
    errors.push({ ...errorObj, index: index });
  });
  return errors;
};

export const validateActivationEditTableData = data => {
  let errors = [];
  data.forEach((value, index) => {
    let errorObj = { ...value };

    // common validation
    const commonValidation = [
      { fieldName: 'url', isUrlCheck: true },
      { fieldName: 'startDate', isDateCheck: true, startDateEndDateCheck: true },
      { fieldName: 'endDate', isDateCheck: true, startDateEndDateCheck: true },
    ];
    errorObj = { ...errorObj, ...validateCSVRowData(commonValidation, value) };

    if (
      value['serviceType'] === SERVICE_TYPE.DISPLAY_ADS ||
      value['serviceType'] === SERVICE_TYPE.DEDICATED_EMAILS ||
      value['serviceType'] === SERVICE_TYPE.GIVEAWAYS
    ) {
      const validationConfig = [{ fieldName: 'url', isRequired: true }];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }

    if (value['serviceType'] === SERVICE_TYPE.SOLD_SLOT) {
      let validationConfig = [
        { fieldName: 'saleDlp', isNumberCheck: true, isRequired: true },
        { fieldName: 'dlp', isNumberCheck: true, isRequired: true },
        { fieldName: 'runDate', isRequired: true },
      ];
      errorObj = { ...errorObj, ...validateCSVRowData(validationConfig, value) };
    }
    errors.push({ ...errorObj, index: index });
  });
  return errors;
};

export const validateCSVRowData = (validationConfig, obj) => {
  let errorObj = {};
  validationConfig.forEach(require => {
    const fieldName = require?.fieldName;
    const fieldValue = obj[fieldName] && String(obj[fieldName])?.trim();

    if (require.checkISBNOrASIN) {
      const primaryISBN = obj['ISBN'] && String(obj['ISBN']).trim();
      const primaryASIN = obj['ASIN'] && String(obj['ASIN']).trim();

      if (!primaryISBN && !primaryASIN) {
        errorObj['ISBN'] = 'At least one of ISBN or ASIN must be provided![[ERROR]]';
        errorObj['ASIN'] = 'At least one of ISBN or ASIN must be provided![[ERROR]]';
        errorObj.isError = true;
      }
    }
    // null check
    if (require.isRequired && !require.isNumberCheck && (!fieldValue || !String(fieldValue)?.trim())) {
      errorObj[fieldName] = 'Field is missing![[ERROR]]';
      errorObj.isError = true;
      return;
    }
    // length check
    if (fieldValue && require.checkLength) {
      if (fieldValue?.length !== require.checkLength) {
        errorObj[fieldName] = `Length should be ${require.checkLength}![[ERROR]]`;
        errorObj.isError = true;
        return;
      }
    }
    // number check
    if (require.isNumberCheck) {
      if (require.isRequired) {
        if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
          errorObj[fieldName] = 'Cannot be empty![[ERROR]]';
          errorObj.isError = true;
          return;
        } else if (fieldValue && isNaN(fieldValue)) {
          errorObj[fieldName] = 'Should be a Number![[ERROR]]';
          errorObj.isError = true;
          return;
        }
      } else if (fieldValue && isNaN(fieldValue)) {
        errorObj[fieldName] = 'Should be a Number![[ERROR]]';
        errorObj.isError = true;
        return;
      }
    }
    // value should be includes in checkValues
    if (fieldValue && require.checkValues && require.checkValues.length > 0) {
      // Some columns (Product Type) are typed by hand, so accept any casing and
      // compare against a lower-cased copy of the allowed values.
      const comparableValue = require.isCaseInsensitive ? fieldValue.toLowerCase() : fieldValue;
      const comparableCheckValues = require.isCaseInsensitive
        ? require.checkValues.map(value => String(value).toLowerCase())
        : require.checkValues;

      if (validateString(comparableValue, comparableCheckValues)) {
        errorObj[fieldName] = `Should be: ${require.checkValues.join(', ')}[[ERROR]]`;
        errorObj.isError = true;
        return;
      }
    }
    // boolean check (true or false)
    if (require.isBooleanCheck) {
      if (fieldValue && !(fieldValue.toLowerCase() === 'true' || fieldValue.toLowerCase() === 'false')) {
        errorObj[fieldName] = 'Should be: true or false[[ERROR]]';
        errorObj.isError = true;
        return;
      }
    }
    // Date check
    if (fieldValue && require.isDateCheck) {
      // Date format should be MM/DD/YYYY
      if (
        (new Date(fieldValue) === 'Invalid Date' && isNaN(new Date(fieldValue))) ||
        !isOrmValidDateFormat(fieldValue, 'MM/DD/YYYY', true)
      ) {
        errorObj[fieldName] = 'Invalid Date![[ERROR]]';
        errorObj.isError = true;
      }
      if (fieldValue && require.startDateEndDateCheck) {
        const startDate = obj['Start Date'] || obj['startDate'] ? new Date(obj['Start Date'] || obj['startDate']) : '';
        const endDate = obj['End Date'] || obj['endDate'] ? new Date(obj['End Date'] || obj['endDate']) : '';
        if (startDate && endDate && startDate > endDate) {
          errorObj[fieldName] = 'End date must be greater than the start date.[[ERROR]]';
          errorObj.isError = true;
        }
      }
      if (fieldValue && require.onlyFutureDateAllowed) {
        const startDateKey = obj['Start Date'] || obj['startDate'];
        const startDate = startDateKey ? new Date(startDateKey) : null;

        const today = new Date();
        const futureDate = new Date(today);
        // Clear time to compare only dates (optional but useful if time doesn't matter)
        futureDate.setHours(0, 0, 0, 0);
        if (startDate) startDate.setHours(0, 0, 0, 0);

        if (startDate && startDate < futureDate) {
          errorObj[fieldName] = 'Date must be future date. [[ERROR]]';
          errorObj.isError = true;
        }
      }

      return;
    }
    // URL check
    if (fieldValue && require.isUrlCheck) {
      if (!isValidURL(fieldValue)) {
        errorObj[fieldName] = 'Invalid Url![[ERROR]]';
        errorObj.isError = true;
        return;
      }
    }

    // Validate a comma-separated retailer list against the known retailer names, ignoring case. "ALL" is valid.
    if (fieldValue && require.isRetailerArrayCheck && require.retailerNames?.length) {
      const known = new Set(require.retailerNames.map(name => String(name).toLowerCase()));
      const invalidRetailers = fieldValue
        .split(',')
        .map(value => value.trim())
        .filter(value => value && value.toUpperCase() !== 'ALL' && !known.has(value.toLowerCase()));
      if (invalidRetailers.length > 0) {
        errorObj[fieldName] = `Invalid Retailer(${invalidRetailers.join(
          ', '
        )})! Should be: ALL, ${require.retailerNames.join(', ')}[[ERROR]]`;
        errorObj.isError = true;
        return;
      }
    }

    // Validate the Country Array
    if (require.isCountryArrayCheck) {
      // Check if field is required but missing
      if (require.isRequired && (!fieldValue || !String(fieldValue)?.trim())) {
        errorObj[fieldName] = 'Field is missing![[ERROR]]';
        errorObj.isError = true;
        return;
      }

      // Validate country codes if field has a value
      if (fieldValue) {
        const validCountryCodes = new Set(countryOptions.map(option => option.value));
        const countries = fieldValue.split(',').map(value => value?.trim());

        // Filter out invalid countries
        const invalidCountries = countries.filter(value => {
          // "ALL" is considered valid
          if (value.toUpperCase() === 'ALL') return false;
          // Country codes not having length of 2 are invalid
          if (value.length !== 2) return true;
          // Country codes of length 2 that are not in the validCountryCodes set are invalid
          return !validCountryCodes.has(value);
        });

        // If any invalid countries are found, add an error to errorObj
        if (invalidCountries.length > 0) {
          errorObj[fieldName] = `Invalid Country Code(${invalidCountries.join(', ')})![[ERROR]]`;
          errorObj.isError = true;
          return;
        }
      }
    }
  });
  return errorObj;
};

//This method will take requiredFields array and data array.
//Output [{title: '(value identifier field)', fieldName: 'errorMessage'}]
export const csvDataValidation = (requiredField, data) => {
  if (requiredField?.length > 0) {
    const identifier = requiredField.find(x => x.isIdentifier);
    const results = [];
    data.forEach(value => {
      let obj = {
        title: value[identifier?.fieldName],
      };
      requiredField.forEach(require => {
        const fieldName = require?.fieldName;
        const fieldValue =
          typeof value[fieldName] === 'number' ? value[fieldName] : value[fieldName]?.toString()?.trim();
        //null check
        if (!fieldValue && require.isRequired) {
          obj = {
            ...obj,
            isError: true,
            [fieldName]: `Field is missing!`,
          };
          return;
        }
        //check length of string
        if (require.checkLength) {
          if (fieldValue?.length !== require.checkLength) {
            obj = {
              ...obj,
              isError: true,
              [fieldName]: `Length should be ${require.checkLength}!`,
            };
            return;
          }
        }
        // number check
        if (require.isNumberCheck) {
          if (isNaN(fieldValue)) {
            obj = {
              ...obj,
              isError: true,
              [fieldName]: `Should be Number!`,
            };
            return;
          }
        }
        //check value should be checkValues
        if (require.checkValues && require.checkValues.length > 0) {
          if (validateString(fieldValue, require.checkValues)) {
            obj = {
              ...obj,
              isError: true,
              [fieldName]: `Should be: ${require.checkValues.join(', ')}`,
            };
            return;
          }
        }
        //check boolean value (tru or false)
        if (require.isBooleanCheck) {
          if (fieldValue && !(fieldValue.toLowerCase() === 'true' || fieldValue.toLowerCase() === 'false')) {
            obj = {
              ...obj,
              isError: true,
              [fieldName]: `Should be: true or false`,
            };
            return;
          }
        }
        //check is it valid date or not
        if (require.isDateCheck) {
          if (new Date(fieldValue) === 'Invalid Date' || isNaN(new Date(fieldValue).getTime())) {
            obj = {
              ...obj,
              isError: true,
              [fieldName]: `Invalid Date!`,
            };
            return;
          }
        }
        obj = {
          ...obj,
          [fieldName]: fieldValue,
        };
      });
      if (Object.keys(obj).find(key => key === 'isError')) {
        results.push(obj);
      }
    });
    return results;
  } else {
    return [];
  }
};

// return true if string is not includes in array
// params string should be coma separated and values should array of string
export const validateString = (string = '', values) => {
  let arrayOfString = string
    ?.trim()
    ?.split(',')
    .map(x => x.trim());
  return arrayOfString.some(value => !values.includes(value));
};

export const formatPartnerTitleCSVData = (data, isActivation = false) => {
  let formatedCSV = [];
  if (data.length) {
    for (let title of data) {
      if (title['Primary ISBN'] || title['ISBN'] || title.primary_isbn || title['ASIN'] || title['asin']) {
        let asin = title['ASIN'] || title['asin'];
        let authors = [];
        let rowData = {
          item_type: 'Book',
          title: title['Title'] || title.title,
          primary_isbn: title.primary_isbn || title['Primary ISBN'] || title['ISBN'],
          asin: asin,
          publisher: title['Company'],
          sale_dlp: title['Sale DLP'] || title['SaleDLP'],
          price:
            title['DLP'] != null && title['DLP'] !== '' && !isNaN(Number(title['DLP'])) ? Number(title['DLP']) : null,
          image: !isActivation ? formatBookCoverImage(title['Book Cover']) : formatBookCoverImage(asin + '.jpg'),
          created: new Date(),
          partner_title: true,
          active: true,
          authordisplayname: title['Authors'] || title.authors,
          google_id: title['Google_Id'],
          newsletter_copy: title['EBB Newsletter Description'] || title.ebbNewsletterDescription,
          pub_date: title.pubDate,
        };
        const productType = title['Product Type'] || title.productType;
        const normalizedProductType = productType ? String(productType).trim().toLowerCase() : '';
        if (normalizedProductType) {
          rowData.productType = normalizedProductType;
        }
        if (title.parentIsbn) {
          rowData.parentIsbn = title.parentIsbn;
        }
        if (title.audio_isbn) {
          rowData.audio_isbn = title.audio_isbn;
        }
        if (title.print_isbn) {
          rowData.print_isbn = title.print_isbn;
        }
        if (normalizedProductType === BOOK_PRODUCT_TYPE_AUDIO) {
          const audibleAsin = title['Audible ASIN'] || title.audible_asin;
          if (audibleAsin) {
            rowData.audible_asin = audibleAsin;
          }
        }
        if (title.BISAC || title.bisac) {
          if (title.BISAC) {
            rowData.bisacs = title['BISAC'].split(',');
          } else {
            rowData.bisacs = title['bisac'].split(',');
          }
        }
        if (isActivation && (title.Countries || title.countries)) {
          rowData.countries = title.Countries || title.countries;
        }

        let authorsArray = [];
        if (title.Authors?.trim()) {
          authorsArray = title.Authors.split(' & ');
        } else if (title.authors?.trim()) {
          authorsArray = title.authors.split(' & ');
        }
        authorsArray && authorsArray.map(author => authors.push({ role: 1, display_name: author }));
        rowData.authors = authors;
        formatedCSV.push(rowData);
      }
    }
  }
  return formatedCSV;
};

export const formatAudioContractCSVData = data => {
  let formattedCSV = [];
  if (data?.length) {
    for (let value of data) {
      const rowData = {
        audioPublisher: value['Audio Publisher'],
        primaryIsbn: value['ISBN-13'],
        ormContractDate: value['ORM Contract Date'],
        ormAudioExpiration: value['ORM Audio Expiration'],
        ormAutoRenew: value['ORM Auto Renew'] ? value['ORM Auto Renew'].toLowerCase() === 'true' : false,
        ormAutoRenewTerm: Number(value['ORM Auto Renew Term']),
        audioPublisherContractDate: value['Audio Publisher Contract Date'],
        audioPublisherExpiration: value['Audio Publisher Expiration'],
        audioPublisherAutoRenew: value['Audio Publisher Auto Renew']
          ? value['Audio Publisher Auto Renew'].toLowerCase() === 'true'
          : false,
        audioPublisherAutoRenewTerm: Number(value['Audio Publisher Auto Renew Term']),
        physicalRoyalties: value['Royalties on Physical']?.split('%')[0],
        digitalRoyalties: value['Royalties on Digital']?.split('%')[0],
        paySchedule: value['Pay Schedule']?.length > 0 ? value['Pay Schedule'].split(', ') : [],
        advance: Number(value['Advance']),
        audioBookPubDate: value['Audiobook Pub Date'],
        language: value['Language'],
        comment: value['Comments'],
        ormContractStatus: value['ORM Contract Status'],
        audioPublisherContractStatus: value['Audio Publisher Contract Status'],
        agent: value['Agent'],
      };
      formattedCSV.push(rowData);
    }
  }
  return formattedCSV;
};

// Function to validate data when Master Price Grid is disabled
export const validateWhenMasterGridDisabled = value => {
  const errorObj = { ...value };

  const allowedCountries = value.allowedCountry
    ?.split(',')
    .map(country => country.trim())
    .filter(Boolean); // Remove empty strings

  // Validate allowedCountry rules
  if (!allowedCountries || allowedCountries.length === 0) {
    return {
      ...errorObj,
      allowedCountry: 'Allowed Country is required when Master Price Grid is disabled.[[ERROR]]',
      isError: true,
    };
  }

  if (allowedCountries[0]?.toUpperCase() === 'ALL') {
    return {
      ...errorObj,
      allowedCountry: 'Allowed Country cannot be "ALL" when Master Price Grid is disabled.[[ERROR]]',
      isError: true,
    };
  }

  if (allowedCountries.length > 2) {
    return {
      ...errorObj,
      allowedCountry: 'Max two country is allowed when Master Price Grid is disabled.[[ERROR]]',
      isError: true,
    };
  }

  return errorObj; // No validation error
};

/**
 * @function validateManualPricelogGenerationCsv
 * @desc    Validates CSV data for Manual Price Log Generation uploads
 * @param   {Object} csvData - Parsed CSV data (from PapaParse or similar)
 * @returns {Object} { hasError: Boolean, errors: Array, toastError: String | null, filteredData: Array }
 */
export const validateManualPricelogGenerationCsv = async csvData => {
  const CSVHeader = csvData.meta.fields.map(x => x.toString().trim());
  const mandatoryColumns = ['Isbn', 'PriceType', 'Currency', 'Price'];
  const fieldDataCheck = [
    { fieldName: 'Isbn', checkLength: 13, isIdentifier: true, isRequired: true },
    { fieldName: 'Price', isNumberCheck: true, isRequired: true },
    { fieldName: 'PriceType', isRequired: true, checkValues: ['AGY', 'AG3', 'MSR', 'RTMSR', 'LIBP', 'US1'] },
    { fieldName: 'Currency', isRequired: true, checkLength: 3 },
    { fieldName: 'EffectiveDate', isDateCheck: true },
  ];

  let headerErrors = validateCsvHeaders(CSVHeader, mandatoryColumns);

  // If CSV is missing mandatory columns, return immediately
  if (headerErrors && headerErrors.length > 0) {
    const msg = 'Column(s) Missing (' + headerErrors.map(x => x).join(', ') + '). CSV rejected !!!';
    return {
      hasError: true,
      errors: [],
      toastError: msg,
      filteredData: [],
    };
  }

  // Remove completely empty rows
  const filteredData = csvData.data.filter(row => Object.values(row).some(value => value.trim() !== ''));

  // Run row-level validations
  let errors = [];
  filteredData.forEach((value, index) => {
    let errorObj = value;

    // Run field-level validation
    errorObj = { ...errorObj, ...validateCSVRowData(fieldDataCheck, value) };

    // If validation failed, add to the error list with row index
    if (errorObj.isError) {
      errors.push({ ...errorObj, index });
    }
  });

  if (errors.length > 0) {
    return {
      hasError: true,
      errors,
      toastError: null,
      filteredData: [],
    };
  }

  return {
    hasError: false,
    errors: [],
    toastError: null,
    filteredData,
  };
};

// String-based "round half up" to 2 decimal places.
// Avoids float representation issues: "1.065" -> "1.07", "1.064" -> "1.06".
// Reads the 3rd decimal digit directly from the string, no float arithmetic.
const roundHalfUp2dp = s => {
  const dot = s.indexOf('.');
  if (dot === -1 || s.slice(dot + 1).length <= 2) return parseFloat(s).toFixed(2);
  const frac = s.slice(dot + 1);
  const carry = parseInt(frac[2], 10) >= 5 ? 1 : 0;
  if (!carry) return `${s.slice(0, dot)}.${frac.slice(0, 2)}`;
  const cents = parseInt(s.slice(0, dot), 10) * 100 + parseInt(frac.slice(0, 2), 10) + 1;
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
};

/**
 * Parses a raw EUR price cell to a 2-decimal string, or '' if blank/invalid.
 *
 * Handles:
 *  - Numeric XLSX cells (typeof number)
 *  - Comma decimal separator  "2,99"   -> "2.99"
 *  - European thousands       "1.299,50" -> "1299.50"
 *  - US thousands             "1,299.50" -> "1299.50"
 *  - Currency symbols         euro/pound/dollar signs stripped
 *  - Blank / whitespace / null / undefined -> ""
 *  - "1.065" -> "1.07"  (3rd decimal >= 5 rounds up, string-based)
 */
export const parseEurPrice = raw => {
  if (raw === null || raw === undefined) return null;
  // Numeric XLSX cell: use toFixed(10) to get a full-precision string, then round from string.
  if (typeof raw === 'number') {
    if (isNaN(raw) || raw <= 0) return null;
    return parseFloat(roundHalfUp2dp(raw.toFixed(10)));
  }
  const s = String(raw)
    .trim()
    .replace(/[€£$  ]/g, '');
  if (!s) return null;
  let normalized;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) {
    normalized =
      s.lastIndexOf(',') > s.lastIndexOf('.')
        ? s.replace(/\./g, '').replace(',', '.') // "1.299,50" -> "1299.50"
        : s.replace(/,/g, ''); // "1,299.50" -> "1299.50"
  } else if (hasComma) {
    normalized = s.replace(',', '.'); // "2,99" -> "2.99"
  } else {
    normalized = s;
  }
  if (isNaN(parseFloat(normalized)) || parseFloat(normalized) <= 0) return null;
  return parseFloat(roundHalfUp2dp(normalized));
};
